import { Box, Center, Spinner, useToast } from '@chakra-ui/react';
import {
  BackofficeGroupPromoListQuery,
  BackofficePromoCustomerType,
  BackofficePromoMechanism,
  BackofficePromoStatus,
  BackofficePromoType,
} from '@diamond/shared/types';
import {
  DropResult,
  LoadingOverlay,
  PageLoader,
  showToast,
  Sortable,
  SortableItem,
} from '@diamond/shared/ui';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import {
  BACKOFFICE_GROUP_PROMO_QUERY_KEY,
  reorderPromo,
  useGroupPromoList,
} from '../api';
import { HeadingListPromo } from './heading-list-promo';
import { PromoItem } from './promo-item';

export interface PromoListProps {
  customer_type: BackofficePromoCustomerType | undefined;
  searchQuery: string;
  start_date?: number;
  end_date?: number;
  mechanism?: BackofficePromoMechanism;
  promo_type?: BackofficePromoType;
  status_promo?: BackofficePromoStatus;
}

type ReorderPromoArgs = {
  id: string;
  destinationIndex: number;
};

export function PromoList(props: PromoListProps) {
  const session = useAuthStore();
  const { ref, inView } = useInView();
  const queryClient = useQueryClient();
  const toast = useToast();

  const queryParams: Record<string, unknown> = {
    customer_type: props.customer_type,
    search_query: props.searchQuery,
    size: 25,
    page: 1,
    // conditionally for params search
    ...(props.start_date && { start_date: props.start_date }),
    ...(props.end_date && { end_date: props.end_date }),
    ...(props.mechanism && { mechanism: props.mechanism }),
    ...(props.promo_type && { promo_type_by_shipto: props.promo_type }),
    ...(props.status_promo && { status: props.status_promo }),
    ...(props.searchQuery && { search_query: props.searchQuery }),
  };

  const {
    data: promo,
    isPending,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    dataUpdatedAt,
  } = useGroupPromoList(queryParams as BackofficeGroupPromoListQuery);

  /**
   * Fetches a minimal, *stable subset* (the first 2 items) of group promotions
   * based *only* on the `customer_type`.
   *
   * **Purpose:**
   * This data (`promoIndexing`) is intentionally fetched *without* applying the
   * user's current search query or filters (like dates, status, mechanism, etc.)
   * that are used for the main `promo` list.
   *
   * This ensures that UI elements relying on this data (e.g., Indexing Button) remain consistent
   * and always refer to the absolute beginning of the *unfiltered* list for this
   * customer type, regardless of how the main list (`promo`) is being filtered
   * or searched by the user. It prevents these elements from disappearing or
   * changing targets when filters yield different results or no results.
   */
  const { data: promoIndexing } = useGroupPromoList({
    customer_type: props.customer_type,
    size: 2,
    page: 1,
    // NOTE: No other filters (search, dates, status, etc.) are passed here intentionally.
  });

  /**
   * Mutation to handle reordering promotions
   */
  const reorderMutation = useMutation({
    mutationFn: ({ id, destinationIndex }: ReorderPromoArgs) =>
      reorderPromo(
        session.access_token,
        { id },
        props.customer_type
          ? { index: destinationIndex, customer_type: props.customer_type }
          : undefined
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [BACKOFFICE_GROUP_PROMO_QUERY_KEY, queryParams],
      });
    },
    onError: (error) => {
      console.error('Error reordering promo:', error);
      showToast(toast, 'error', error.message);
    },
  });

  const indexArray = promoIndexing?.pages[0].data.map((item) => item.index);

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, hasNextPage, isFetchingNextPage]);

  if (isPending) {
    return <PageLoader />;
  }

  const promotionsActive: SortableItem[] =
    promo?.pages.flatMap((page) =>
      page.data.map((promo) => {
        return {
          id: promo.id,
          originalIndex: promo.index,
          children: (
            <PromoItem item={promo} customer_type={props.customer_type} />
          ),
        };
      })
    ) ?? [];

  /**
   * Handle drag and drop reordering of promotions
   */
  const handleIndexChange = (result: DropResult, items: SortableItem[]) => {
    if (!result.destination) return;
    const destinationIdx = items[result.destination.index].originalIndex;
    if (typeof destinationIdx === 'undefined') return;
    reorderMutation.mutate({
      id: result.draggableId,
      destinationIndex: destinationIdx,
    });
  };

  /**
   * Handle reordering via indexing button click
   */
  const handleClickIndexing = (id: string) => {
    if (indexArray) {
      reorderMutation.mutate({
        id: id,
        destinationIndex: indexArray[1],
      });
    }
  };

  return (
    <>
      <LoadingOverlay
        isLoading={reorderMutation.isPending}
        text="Mengubah urutan promo..."
      />
      <Box my={8} width="full">
        <HeadingListPromo />
        <Sortable
          key={dataUpdatedAt}
          data={promotionsActive}
          handleChange={handleIndexChange}
          shouldRefetchData={false}
          isPromotion={true}
          handleClickIndexing={handleClickIndexing}
          pageLoadedIndex={indexArray}
          showIndexingButton={true}
          disableReorder={!props.customer_type}
        />
        <Box ref={ref}></Box>
        {isFetchingNextPage && (
          <Center mt={4}>
            <Spinner />
          </Center>
        )}
        {!isPending && promotionsActive.length === 0 && (
          <Center mt={10}>
            <Box as="p" color="gray.500" data-testid="empty-state-message">
              Tidak ada promo yang cocok dengan filter.
            </Box>
          </Center>
        )}
      </Box>
    </>
  );
}
